import React, { useEffect, useState } from 'react';
import { Breadcrumb, Card, Form, Input, Button, Row, Col, Typography, message, Space, Spin } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import AttachmentManager from '../components/sop/AttachmentManager';
import MessagePreview from '../components/sop/MessagePreview';
import fileService from '../services/fileService';
import weworkGroupWelcomeTemplateService from '../services/weworkGroupWelcomeTemplateService';

const { Title } = Typography;
const { TextArea } = Input;

const WeworkGroupWelcomeTemplateEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState([{ id: 'text_1', type: 'TEXT', content: '' }]);
  const [previewUrls, setPreviewUrls] = useState({});
  const [uploading, setUploading] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [nextAttachmentId, setNextAttachmentId] = useState(2);

  useEffect(() => {
    if (isEdit) {
      loadDetail();
    }
  }, [id]);

  const loadDetail = async () => {
    setLoading(true);
    try {
      const res = await weworkGroupWelcomeTemplateService.getById(id);
      if (res.success && res.data) {
        const t = res.data;
        form.setFieldsValue({ name: t.name || '' });
        const content = t.content || {};
        // 支持两种存储结构：
        // 1) 新：顶层存储 { textContent: string, attachments: [...] }
        // 2) 旧：与企微结构对齐的 content = { text: {content}, image/link/miniprogram/file/video }
        const normalize = (att) => {
          if (!att) return null;
          const base = { id: `att_${Date.now()}_${Math.random()}` };
          if (att.type === 'TEXT') return { ...base, type: 'TEXT', content: att.content || '' };
          if (att.type === 'IMAGE_GROUP') return { ...base, type: 'IMAGE_GROUP', imageGroup: { mediaIds: Array.isArray(att.imageGroup?.mediaIds) ? att.imageGroup.mediaIds : [], ossUrls: att.imageGroup?.ossUrls || {} } };
          if (att.type === 'VIDEO') return { ...base, type: 'VIDEO', mediaId: att.mediaId || '', ossUrl: att.ossUrl || '' };
          if (att.type === 'FILE') return { ...base, type: 'FILE', mediaId: att.mediaId || '', ossUrl: att.ossUrl || '' };
          if (att.type === 'LINK') return { ...base, type: 'LINK', title: att.title || '', description: att.description || '', url: att.url || '', picUrl: att.picUrl || att.pic_url || '' };
          if (att.type === 'MINIPROGRAM') return { ...base, type: 'MINIPROGRAM', title: att.title || '', appid: att.appid || '', pagepath: att.pagepath || '', thumbMediaId: att.thumbMediaId || '', thumbMediaId_ossUrl: att.thumbMediaId_ossUrl || '' };
          return null;
        };

        let next = [];
        // 优先读取顶层字段
        const topText = typeof t.textContent === 'string' ? t.textContent : '';
        const topAtts = Array.isArray(t.attachments) ? t.attachments : [];
        if (topText || topAtts.length > 0) {
          next.push({ id: 'text_1', type: 'TEXT', content: topText });
          const others = topAtts.map(normalize).filter(Boolean);
          next = next.concat(others);
        } else {
          // 兼容旧结构
          next = [{ id: 'text_1', type: 'TEXT', content: content.text?.content || '' }];
          if (content.image) next.push({ id: 'att_2', type: 'IMAGE_GROUP', imageGroup: { mediaIds: [content.image.media_id].filter(Boolean), ossUrls: content.image.pic_url ? { [content.image.media_id]: content.image.pic_url } : {} } });
          else if (content.link) next.push({ id: 'att_2', type: 'LINK', title: content.link.title, description: content.link.desc, url: content.link.url });
          else if (content.miniprogram) next.push({ id: 'att_2', type: 'MINIPROGRAM', title: content.miniprogram.title, appid: content.miniprogram.appid, pagepath: content.miniprogram.page, thumbMediaId: content.miniprogram.pic_media_id });
          else if (content.file) next.push({ id: 'att_2', type: 'FILE', mediaId: content.file.media_id });
          else if (content.video) next.push({ id: 'att_2', type: 'VIDEO', mediaId: content.video.media_id });
        }
        setAttachments(next);
        setNextAttachmentId(next.length + 1);
      }
    } catch (e) {
      message.error(e.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAttachment = (type) => {
    const id = `att_${nextAttachmentId}`;
    const att = { id, type };
    if (type === 'IMAGE_GROUP') att.imageGroup = { mediaIds: [], ossUrls: {} };
    if (type === 'VIDEO' || type === 'FILE') { att.mediaId = ''; att.ossUrl = ''; }
    if (type === 'LINK') { att.title = ''; att.description = ''; att.url = ''; }
    if (type === 'MINIPROGRAM') { att.title = ''; att.appid = ''; att.pagepath = ''; att.thumbMediaId = ''; att.thumbMediaId_ossUrl = ''; }
    setAttachments(prev => [...prev, att]);
    setNextAttachmentId(prev => prev + 1);
    return id;
  };

  const handleRemoveAttachment = (attachmentId) => {
    setAttachments(prev => prev.filter(a => a.id !== attachmentId));
  };

  const handleUpdateAttachmentContent = (attachmentId, field, value) => {
    setAttachments(prev => prev.map(a => (a.id === attachmentId ? { ...a, [field]: value } : a)));
  };

  const handleFileUpload = async ({ file, onSuccess, onError, onProgress, fieldKey, mediaType, stepType = 'MESSAGE' }) => {
    try {
      const uploadKey = `${stepType}_${fieldKey}`;
      setUploading(prev => ({ ...prev, [uploadKey]: true, [fieldKey]: true }));
      setUploadProgress(prev => ({ ...prev, [uploadKey]: 0, [fieldKey]: 0 }));

      const updateProgress = (percent) => {
        setUploadProgress(prev => ({ ...prev, [uploadKey]: percent, [fieldKey]: percent }));
        if (onProgress) onProgress({ percent });
      };

      const tenantStr = localStorage.getItem('tenant');
      if (!tenantStr) {
        message.error('未获取到租户信息，请重新登录');
        if (onError) onError(new Error('未获取到租户信息'));
        return;
      }
      const tenant = JSON.parse(tenantStr);
      const tenantId = tenant.id;

      const result = await fileService.uploadMedia(file, mediaType, tenantId, 'message', updateProgress);
      if (result?.success && result.mediaId) {
        setPreviewUrls(prev => ({ ...prev, [result.mediaId]: result.ossUrl }));
        message.success(`${file.name} 上传成功`);
        if (onSuccess) onSuccess(result);
        return result;
      } else {
        const errorMsg = result?.message || '上传失败';
        message.error(errorMsg);
        if (onError) onError(new Error(errorMsg));
      }
    } catch (error) {
      console.error('文件上传失败:', error);
      message.error(`${file?.name || '文件'} 上传失败: ${error.message}`);
      if (onError) onError(error);
    } finally {
      const uploadKey = `MESSAGE_${fieldKey}`;
      setUploading(prev => ({ ...prev, [uploadKey]: false, [fieldKey]: false }));
    }
  };

  const handleRemoveFile = (fieldKey, stepType = 'MESSAGE') => {
    const uploadKey = `${stepType}_${fieldKey}`;
    setUploading(prev => ({ ...prev, [uploadKey]: false, [fieldKey]: false }));
    setUploadProgress(prev => ({ ...prev, [uploadKey]: 0, [fieldKey]: 0 }));
    const target = (Array.isArray(attachments) ? attachments : []).find(att => att && att.id === fieldKey);
    if (target && target.mediaId) {
      setPreviewUrls(prev => {
        const next = { ...prev };
        delete next[target.mediaId];
        return next;
      });
    }
  };

  const handleUpdatePreviewUrls = (map) => {
    if (!map || typeof map !== 'object') return;
    setPreviewUrls(prev => ({ ...prev, ...map }));
  };

  const insertNickname = () => {
    setAttachments(prev => {
      const list = Array.isArray(prev) ? prev.slice() : [];
      const idx = list.findIndex(a => a && a.type === 'TEXT');
      if (idx === -1) return [{ id: 'text_1', type: 'TEXT', content: '%NICKNAME%' }, ...list];
      const target = list[idx];
      const before = target.content || '';
      list[idx] = { ...target, content: `${before}%NICKNAME%` };
      return list;
    });
  };

  const onSubmit = async (values) => {
    setSaving(true);
    try {
      const text = attachments.find(a => a.type === 'TEXT')?.content || '';
      const others = attachments.filter(a => a.type !== 'TEXT');
      // 发送顶层字段，后端不再使用 content 持久化
      const payload = { name: values.name, textContent: text, attachments: others };
      let res;
      if (isEdit) res = await weworkGroupWelcomeTemplateService.update(id, payload);
      else res = await weworkGroupWelcomeTemplateService.create(payload);

      if (res.success) {
        message.success(isEdit ? '更新成功' : '创建成功');
        navigate('/wework-group-welcome-templates');
      } else {
        message.error(res.message || '保存失败');
      }
    } catch (e) {
      message.error(e.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin />
      </div>
    );
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Breadcrumb>
            <Breadcrumb.Item onClick={() => navigate('/wework-group-welcome-templates')}>
              <Title level={5} style={{ cursor: 'pointer', color: '#1677ff', margin: 0 }}>入群欢迎语素材</Title>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Title level={5} style={{ margin: 0 }}>{isEdit ? '编辑' : '新建'}</Title>
            </Breadcrumb.Item>
          </Breadcrumb>
        </Col>
      </Row>
      <Card>
        <Form form={form} layout="vertical" onFinish={onSubmit} initialValues={{ name: '' }}>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
                <Input placeholder="请输入素材名称，便于运营识别" maxLength={64} showCount />
              </Form.Item>

              <Space style={{ marginBottom: 12 }}>
                <Button size="small" onClick={insertNickname}>插入客户昵称 (%NICKNAME%)</Button>
              </Space>

              <AttachmentManager
                attachments={attachments}
                onUpdateContent={handleUpdateAttachmentContent}
                onAddAttachment={handleAddAttachment}
                onRemoveAttachment={handleRemoveAttachment}
                previewUrls={previewUrls}
                uploading={uploading}
                uploadProgress={uploadProgress}
                handleFileUpload={handleFileUpload}
                handleRemoveFile={handleRemoveFile}
                handleUpdatePreviewUrls={handleUpdatePreviewUrls}
                stepType="MESSAGE"
                maxNonTextAttachments={1}
                materialSelectorMultiSelect={false}
              />
            </Col>
            <Col span={12}>
              <MessagePreview
                step={{
                  type: 'MESSAGE',
                  actionType: 'SEND_MESSAGE',
                  messageList: attachments.map(att => {
                    if (att.type === 'TEXT') return { type: 'TEXT', params: { content: att.content || '' } };
                    if (att.type === 'IMAGE_GROUP') return { type: 'IMAGE_GROUP', params: { imageGroup: att.imageGroup || { mediaIds: [], ossUrls: {} } } };
                    if (att.type === 'VIDEO') return { type: 'VIDEO', params: { mediaId: att.mediaId || '', ossUrl: att.ossUrl || '' } };
                    if (att.type === 'LINK') return { type: 'LINK', params: { title: att.title || '', url: att.url || '', description: att.description || '' } };
                    if (att.type === 'MINIPROGRAM') return { type: 'MINIPROGRAM', params: { title: att.title || '', appid: att.appid || '', pagepath: att.pagepath || '', thumbMediaId: att.thumbMediaId || '', thumbMediaId_ossUrl: att.thumbMediaId_ossUrl || '' } };
                    if (att.type === 'FILE') return { type: 'FILE', params: { mediaId: att.mediaId || '', ossUrl: att.ossUrl || '' } };
                    return null;
                  }).filter(Boolean)
                }}
                noTruncate
              />
            </Col>
          </Row>
          <div style={{ marginTop: 24, display: 'flex', justifyContent: 'center' }}>
            <Space>
              <Button type="primary" htmlType="submit" loading={saving}>保存</Button>
              <Button onClick={() => navigate('/wework-group-welcome-templates')}>取消</Button>
            </Space>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default WeworkGroupWelcomeTemplateEdit;


